import React from 'react';
import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';
import {FieldProps} from "formik";
import {premiumFormControlClassName} from "../formControlStyles";

interface DatePickerFieldProps extends FieldProps<Date | null> {
  onDateChanged?: (date: Date | null) => void;
  excludedDates?: Date[];
  onVisibleMonthChange?: (date: Date) => void;
}

const DatePickerField: React.FC<DatePickerFieldProps> = ({
  field,
  form,
  onDateChanged,
  excludedDates = [],
  onVisibleMonthChange
}) => {
  const today = new Date();

  // Generate an array of the next 7 days
  const next7Days = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    return date;
  });

  const allExcludedDates = [...next7Days, ...excludedDates];
  const isDateExcluded = (date: Date) => allExcludedDates.some((excludedDate) => excludedDate.toDateString() === date.toDateString());

  return (
      <DatePicker
          selected={(field.value && new Date(field.value)) || null}
          onChange={(date) => {
            form.setFieldValue(field.name, date);
            if (onDateChanged) {
              onDateChanged(date); // Call the callback with the selected date
            }
          }}
          dateFormat="yyyy/MM/dd"
          minDate={today} // Disable past dates
          excludeDates={allExcludedDates}
          onMonthChange={(date) => onVisibleMonthChange?.(date)}
          placeholderText="Please Select"
          showPopperArrow={false}
          calendarClassName="fred-datepicker"
          popperClassName="fred-datepicker-popper"
          dayClassName={(date) => {
            const isExcluded = isDateExcluded(date);
            const isPastDate = date < new Date(today.getFullYear(), today.getMonth(), today.getDate());

            if (isExcluded || isPastDate) {
              return "fred-datepicker-day-disabled";
            }

            return "fred-datepicker-day";
          }}
          renderCustomHeader={({
            date,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled
          }) => (
              <div className="fred-datepicker-header">
                <button
                    type="button"
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                    className="fred-datepicker-nav"
                    aria-label="Previous month"
                >
                  <span aria-hidden="true">&lt;</span>
                </button>
                <div className="fred-datepicker-title">
                  {date.toLocaleString('en-NZ', { month: 'long', year: 'numeric' })}
                </div>
                <button
                    type="button"
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                    className="fred-datepicker-nav"
                    aria-label="Next month"
                >
                  <span aria-hidden="true">&gt;</span>
                </button>
              </div>
          )}
          className={premiumFormControlClassName}
      />
  );
};

export default DatePickerField;
